import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { IconChevronLeft, IconChevronRight, IconRefresh } from "@humansignal/icons";
import { Button, Select } from "@humansignal/ui";
import { useFixedLocation, useParams } from "../../providers/RoutesProvider";
import { useProject } from "../../providers/ProjectProvider";
import { ProjectNav } from "../ProjectDashboard/ProjectNav";
import { TabHeader } from "../../components/TabHeader/TabHeader";
import { cn } from "../../utils/bem";
import { FieldsModal } from "./FieldsModal";
import { useExportDownload } from "./useExportDownload";
import { useExportSample, PAGE } from "./useExportSample";
import { useExportLens } from "./useExportLens";
import { useAPI } from "../../providers/ApiProvider";
import "./ExportPreviewPage.prefix.css";

/**
 * Export preview — the annotation output visualizer.
 *
 * Same page chrome as every project tab (TabHeader + the shared
 * ``tab-toolbar``), then two cards: the task rendered read-only and the
 * record the file will contain, linked by selection.
 *
 * URL: /projects/:id/export-preview?view=<dm-view>&format=JSON
 */

const FORMATS = ["JSON", "JSON_MIN", "CSV", "TSV", "COCO", "YOLO", "CONLL2003", "ASR_MANIFEST"];

const CHOICES = [
  { value: "all", label: "All annotations" },
  { value: "first", label: "First annotation" },
  { value: "latest", label: "Latest annotation" },
  { value: "accepted", label: "Accepted only" },
];

const block = cn("export-preview-page");

const usePhone = () => {
  const [phone, setPhone] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia?.("(max-width: 700px)")?.matches,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 700px)");
    const onChange = () => setPhone(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return phone;
};

export const ExportPreviewPage = () => {
  const history = useHistory();
  const api = useAPI();
  const location = useFixedLocation();
  const params = useParams();
  const { project } = useProject();
  const projectId = params?.id ?? project?.id;
  const search = new URLSearchParams(location.search || "");
  const viewId = search.get("view");

  const [format, setFormat] = useState(search.get("format") || "JSON");
  const [annotationChoice, setAnnotationChoice] = useState("all");
  const [fields, setFields] = useState(null); // null = everything
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const phone = usePhone();

  const sample = useExportSample({ projectId, viewId, annotationChoice, fields });
  // Formats the converter actually supports for this labeling config;
  // the hardcoded list is only the fallback while it loads.
  const [formatNames, setFormatNames] = useState(FORMATS);
  useEffect(() => {
    if (!projectId) return;
    api.callApi("exportFormats", { params: { pk: projectId }, errorFilter: () => true }).then((res) => {
      if (Array.isArray(res)) {
        const names = res.filter((f) => !f.disabled).map((f) => f.name);
        if (names.length) setFormatNames(names);
      }
    });
  }, [api, projectId]);
  const formatOptions = formatNames.map((f) => ({ value: f, label: f }));
  const { lens, loading: lensLoading } = useExportLens({ projectId, format, shapedTask: sample.shapedTask });
  const lensDropped = lens?.results ? (rid) => lens.results[rid] === false : () => false;
  const { download, downloading } = useExportDownload(projectId);

  const exportable = Math.max(0, sample.total - sample.skipped);
  const fieldCount = fields ? fields.filter((p) => p !== "annotations.result").length : null;
  const backToDm = () => history.push(`/projects/${projectId}/data${viewId ? `?tab=${viewId}` : ""}`);

  const record = sample.shapedTask
    ? Object.fromEntries(Object.entries(sample.shapedTask).filter(([k]) => k !== "annotations"))
    : null;
  const annotationMeta = sample.annotation
    ? Object.fromEntries(Object.entries(sample.annotation).filter(([k]) => k !== "result"))
    : null;

  return (
    <div className={block.toClassName()}>
      <TabHeader
        title="Export preview"
        subtitle="Exactly what the file will contain — same scope, same shape, one task at a time."
        actions={
          phone ? (
            <div className={block.elem("phone-actions").toClassName()}>
              <Select
                value={format}
                options={formatOptions}
                onChange={(v) => setFormat(v?.value ?? v)}
                triggerClassName={block.elem("select").toClassName()}
              />
              <Button
                onClick={() => download({ format, viewId, shape: { annotationChoice, fields } })}
                waiting={downloading}
                aria-label="Export data"
              >
                {sample.total ? `Export ${exportable.toLocaleString()}` : "Export"}
              </Button>
              <Button look="string" onClick={backToDm} className={block.elem("back").toClassName()}>
                <IconChevronLeft /> Back to Data Manager
              </Button>
            </div>
          ) : (
            <>
              <Select
                value={format}
                options={formatOptions}
                onChange={(v) => setFormat(v?.value ?? v)}
                triggerClassName={block.elem("select").toClassName()}
              />
              <Button look="outlined" onClick={backToDm}>
                Back to Data Manager
              </Button>
              <Button
                onClick={() => download({ format, viewId, shape: { annotationChoice, fields } })}
                waiting={downloading}
                aria-label="Export data"
              >
                {sample.total ? `Export ${exportable.toLocaleString()} tasks` : "Export"}
              </Button>
            </>
          )
        }
      />

      {phone ? (
        <div
          className={`${block.elem("toolbar").mod({ phone: true }).toClassName()} ${cn("tab-toolbar").toClassName()}`}
        >
          <div className={block.elem("row").toClassName()}>
            <span className={block.elem("scope").toClassName()}>
              {sample.loading && !sample.total
                ? "Loading…"
                : viewId
                  ? `${sample.total.toLocaleString()} task${sample.total === 1 ? "" : "s"} in view`
                  : `${sample.total.toLocaleString()} task${sample.total === 1 ? "" : "s"}`}
              {sample.skipped > 0 && (
                <span className={block.elem("drop").toClassName()}> ({sample.skipped.toLocaleString()} dropped)</span>
              )}
            </span>
            <span className={block.elem("nav").toClassName()}>
              <Button
                type="button"
                variant="neutral"
                look="outlined"
                size="small"
                disabled={sample.loading || sample.pos <= 1}
                onClick={sample.prev}
                aria-label="Previous task"
              >
                <IconChevronLeft />
              </Button>
              <span className={block.elem("pos").toClassName()}>
                {sample.pos} / {sample.total}
              </span>
              <Button
                type="button"
                variant="neutral"
                look="outlined"
                size="small"
                disabled={sample.loading || sample.pos >= sample.total}
                onClick={sample.next}
                aria-label="Next task"
              >
                <IconChevronRight />
              </Button>
              <Button
                type="button"
                variant="neutral"
                look="outlined"
                size="small"
                disabled={sample.loading}
                onClick={sample.shuffle}
                aria-label="Shuffle sample"
              >
                <IconRefresh />
              </Button>
            </span>
          </div>
          <div className={block.elem("row").mod({ controls: true }).toClassName()}>
            <Select
              value={annotationChoice}
              options={CHOICES}
              onChange={(v) => setAnnotationChoice(v?.value ?? v)}
              triggerClassName={block.elem("select").toClassName()}
            />
            <Button type="button" variant="neutral" look="outlined" size="small" onClick={() => setFieldsOpen(true)}>
              {fieldCount == null ? "Fields (default)" : `Fields (${fieldCount})`}
            </Button>
          </div>
        </div>
      ) : (
        <div className={`${block.elem("toolbar").toClassName()} ${cn("tab-toolbar").toClassName()}`}>
          <span className={block.elem("scope").toClassName()}>
            {sample.loading && !sample.total ? (
              "Loading…"
            ) : (
              <>
                <span className={block.elem("scope-full").toClassName()}>
                  {viewId
                    ? `${sample.total.toLocaleString()} task${sample.total === 1 ? "" : "s"} in the current Data Manager view`
                    : `${sample.total.toLocaleString()} annotated task${sample.total === 1 ? "" : "s"}`}
                </span>
                <span className={block.elem("scope-short").toClassName()}>
                  {viewId
                    ? `${sample.total.toLocaleString()} task${sample.total === 1 ? "" : "s"} in view`
                    : `${sample.total.toLocaleString()} task${sample.total === 1 ? "" : "s"}`}
                </span>
              </>
            )}
            {sample.skipped > 0 && (
              <span className={block.elem("drop").toClassName()}> ({sample.skipped.toLocaleString()} dropped)</span>
            )}
          </span>
          <Select
            value={annotationChoice}
            options={CHOICES}
            onChange={(v) => setAnnotationChoice(v?.value ?? v)}
            triggerClassName={block.elem("select").toClassName()}
          />
          <span className={block.elem("scope").toClassName()}>
            {fieldCount == null ? "Default fields" : `${fieldCount} fields`}
          </span>
          <Button type="button" variant="neutral" look="outlined" size="small" onClick={() => setFieldsOpen(true)}>
            Choose fields…
          </Button>
          {fields && (
            <Button type="button" variant="neutral" look="string" size="small" onClick={() => setFields(null)}>
              Reset fields
            </Button>
          )}
          <span className={block.elem("spacer").toClassName()} />
          <Button
            type="button"
            variant="neutral"
            look="outlined"
            size="small"
            disabled={sample.loading || sample.pos <= 1}
            onClick={sample.prev}
            aria-label="Previous task"
          >
            <IconChevronLeft />
          </Button>
          <span className={block.elem("pos").toClassName()}>
            {sample.pos} / {sample.total}
          </span>
          <Button
            type="button"
            variant="neutral"
            look="outlined"
            size="small"
            disabled={sample.loading || sample.pos >= sample.total}
            onClick={sample.next}
            aria-label="Next task"
          >
            <IconChevronRight />
          </Button>
          <Button
            type="button"
            variant="neutral"
            look="outlined"
            size="small"
            disabled={sample.loading}
            onClick={sample.shuffle}
            tooltip={`New random sample of ${PAGE}`}
            aria-label="Shuffle sample"
          >
            <IconRefresh />
          </Button>
        </div>
      )}

      {/* Two cards */}
      <div className={block.elem("panes").toClassName()}>
        <section className={block.elem("card").toClassName()}>
          <header className={block.elem("card-head").toClassName()}>
            <div className={block.elem("card-title").toClassName()}>
              {sample.rawTask
                ? `Task ${sample.rawTask.id}`
                : sample.loading
                  ? "Loading sample…"
                  : "No tasks in this export"}
              {sample.annotation?.completed_by != null && (
                <span className={block.elem("pill").toClassName()}>
                  {sample.annotation.completed_by?.email ?? String(sample.annotation.completed_by)}
                </span>
              )}
              {sample.annotation?.review_state && (
                <span className={block.elem("pill").toClassName()}>{sample.annotation.review_state}</span>
              )}
              {sample.rawTask && !sample.annotation && (
                <span className={block.elem("pill").mod({ warn: true }).toClassName()}>dropped — not in the file</span>
              )}
            </div>
          </header>
          <div ref={sample.rootRef} className={block.elem("editor").toClassName()} />
        </section>

        <section className={block.elem("card").mod({ dark: true }).toClassName()}>
          <header className={block.elem("card-head").toClassName()}>
            <div className={block.elem("card-title").toClassName()}>
              Record
              <span className={block.elem("pill").toClassName()}>{format}</span>
            </div>
            <span className={block.elem("hint").toClassName()}>select a region or a result to link them</span>
          </header>
          {format !== "JSON" && sample.shapedTask && (
            <div className={block.elem("lens").toClassName()}>
              {lensLoading && !lens ? (
                <span>Converting through {format}…</span>
              ) : lens?.error ? (
                <span className={block.elem("pill").mod({ warn: true }).toClassName()}>{lens.error}</span>
              ) : lens ? (
                <>
                  <span>
                    Through <b>{format}</b>: <b>{lens.kept}</b> of <b>{lens.total}</b> results survive
                  </span>
                  {(lens.dropped ?? []).map((d) => (
                    <span key={d.type} className={block.elem("pill").mod({ warn: true }).toClassName()}>
                      {d.count} {d.type} {d.count === 1 ? "result" : "results"} won't appear in {format}
                    </span>
                  ))}
                  {lens.note && <span className={block.elem("lens-note").toClassName()}>{lens.note}</span>}
                </>
              ) : null}
            </div>
          )}
          <div className={block.elem("record").toClassName()}>
            {record && (
              <pre className={block.elem("block").mod({ dim: true }).toClassName()}>
                {JSON.stringify(record, null, 2)}
              </pre>
            )}
            {annotationMeta && (
              <pre className={block.elem("block").mod({ dim: true }).toClassName()}>
                {JSON.stringify(annotationMeta, null, 2)}
              </pre>
            )}
            {sample.results.length === 0 ? (
              <div className={block.elem("empty").toClassName()}>{sample.shapedTask ? '"result": []' : "—"}</div>
            ) : (
              sample.results.map((r, i) => {
                const rid = r.id ?? `#${i}`;
                const active = rid === sample.selectedResultId;
                return (
                  <pre
                    key={rid}
                    className={block
                      .elem("block")
                      .mod({ active, dropped: lensDropped(rid) })
                      .toClassName()}
                    data-lens={lensDropped(rid) ? `not in ${format}` : undefined}
                    onClick={() => sample.selectRegion(rid)}
                  >
                    {JSON.stringify(r, null, 2)}
                  </pre>
                );
              })
            )}
            {format !== "JSON" && lens?.outputs?.length > 0 && (
              <>
                {lens.outputs.map((o) => (
                  <div key={o.name}>
                    <div className={block.elem("out-head").toClassName()}>
                      File output
                      <span className={block.elem("pill").toClassName()}>{o.name}</span>
                      {o.binary && <span>binary, {o.size.toLocaleString()} bytes</span>}
                      {o.truncated && <span>(first {Math.round(o.text.length / 1000)}k characters)</span>}
                    </div>
                    {!o.binary && (
                      <pre className={`${block.elem("block").toClassName()} ${block.elem("out").toClassName()}`}>
                        {o.text}
                      </pre>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </section>
      </div>

      <FieldsModal
        key={fieldsOpen ? "open" : "closed"}
        visible={fieldsOpen}
        inventory={sample.inventory}
        fields={fields}
        onApply={setFields}
        onClose={() => setFieldsOpen(false)}
      />
    </div>
  );
};

ExportPreviewPage.title = "Export preview";
// A sibling of the Data Manager (not nested under it): the DM route is
// not exact, so anything under /data renders the DM on top.
ExportPreviewPage.path = "/export-preview";
ExportPreviewPage.exact = true;
ExportPreviewPage.context = () => {
  const { project } = useProject();
  if (!project?.id) return null;
  return <ProjectNav projectId={project.id} canManage={true} project={project} />;
};
